import { generatePath } from '@helpers';
import { z } from 'zod';

import { createSafeEnum } from './utils';

export const listSchema = z.object({
  id: z.string(),
  name: z.string(),
  last_update: z.string(),
});

const watchlistSearchTypeEnum = z.enum(['list', 'public-list']);
export const watchlistSearchType = createSafeEnum(
  watchlistSearchTypeEnum,
  'list'
);

export const watchlistSearchResultSchema = z.object({
  type: watchlistSearchType,
  id: z.string(),
  name: z.string(),
});

export const watchlistSearchResponseSchema = z.object({
  results: z.array(watchlistSearchResultSchema),
});

export type WatchlistSearchResult = z.infer<typeof watchlistSearchResultSchema>;
export type WatchlistSearchResponse = z.infer<
  typeof watchlistSearchResponseSchema
>;

export interface WatchlistSearchParams {
  q: string;
  page?: number;
  page_size?: number;
}

const entitySchema = z.object({
  tag: z.string(),
});

export const watchlistEntitySchema = z.object({
  id: z.string(),
  name: z.string(),
  entities: z.array(entitySchema),
});

const watchlistRelationSchema = z.object({
  tag: z.string(),
  watchlist: z.array(z.string()).optional(),
  competitor_of: z.array(z.string()).optional(),
});

const watchlistRelationsSchema = z.object({
  data: z.array(watchlistRelationSchema),
});
export type WatchlistRelation = z.infer<typeof watchlistRelationSchema>;
export type WatchlistRelations = z.infer<typeof watchlistRelationsSchema>;

const watchlistRelationsBody = z.object({
  watchlists: z.array(
    z.object({
      id: z.string(),
      include_competitors: z.boolean(),
    })
  ),
});

// TODO: add icon_url field once watchlist/v2/templates endpoint contract is finalised.
const watchlistTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  count: z.number(),
});

export type WatchlistRelationsBody = z.infer<typeof watchlistRelationsBody>;
export type WatchlistEntity = z.infer<typeof entitySchema>;
export type WatchlistTemplate = z.infer<typeof watchlistTemplateSchema>;

export const watchlist = {
  getLists: {
    path: 'watchlist/v1',
    schema: z.array(listSchema),
  },
  getList: {
    path: (id: string) => `watchlist/v1/${id}`,
    schema: watchlistEntitySchema,
  },
  postList: {
    path: 'watchlist/v1',
    schema: watchlistEntitySchema,
  } as const,
  putList: {
    path: (id: string) => `watchlist/v1/${id}`,
    schema: watchlistEntitySchema,
  } as const,
  deleteList: {
    path: (id: string) => `watchlist/v1/${id}`,
  } as const,
  postListEntities: {
    path: (id: string) => `watchlist/v1/${id}`,
    schema: entitySchema,
  },
  deleteListEntities: {
    path: (id: string, tag: string) =>
      `watchlist/v1/${id}/entity/${tag}` as const,
  },
  getListsByEntities: {
    path: (tag: string) => `watchlist/v1/entity-lists/${tag}` as const,
    schema: z.array(listSchema),
  },
  getPublicLists: {
    path: () => `watchlist/v1/public`,
    schema: z.array(listSchema),
  },
  getPublicListEntities: {
    path: (listId: string) => `watchlist/v1/public/${listId}`,
    schema: watchlistEntitySchema,
  },
  searchLists: {
    path: ({ q, page, page_size }: WatchlistSearchParams) => {
      const params: Record<string, string | number> = { q };
      if (page !== undefined) {
        params.page = page;
      }
      if (page_size !== undefined) {
        params.page_size = page_size;
      }
      return generatePath({
        basePath: 'watchlist/v3/search',
        params,
      });
    },
    schema: watchlistSearchResponseSchema,
  },
  postListRelations: {
    path: `watchlist/v1/relations`,
    schema: watchlistRelationsSchema,
  },
  getWatchlistTemplates: {
    path: (countryCode: string) =>
      `watchlist/v2/templates?country=${countryCode}`,
    schema: z.array(watchlistTemplateSchema),
  },
} as const;

export type WatchList = z.infer<typeof listSchema>;
export type WatchListEntities = z.infer<typeof watchlistEntitySchema>;
